import React from "react";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  Sparkles,
  BarChart3,
  MousePointerClick,
  Handshake,
  Clock,
  Euro,
  Compass,
} from "lucide-react";
import { motion } from "framer-motion";

const CALENDLY_URL = "https://calendly.com/agence-chlo-ine/30min";

export default function PCConsultingLanding() {
  const problems = [
    {
      icon: Clock,
      title: "Vous manquez de temps",
      text: "Vous pilotez déjà tout : produit, clients, logistique, marketing, site, prestataires. L’e-commerce avance, mais trop lentement.",
    },
    {
      icon: Users,
      title: "Vous manquez de ressources senior",
      text: "Vous avez besoin de recul stratégique, mais recruter une direction e-commerce ou acquisition coûte trop cher à ce stade.",
    },
    {
      icon: BarChart3,
      title: "Vous manquez de pilotage clair",
      text: "Les ventes existent, mais les priorités sont floues : acquisition, marge, conversion, CRM, créa, ads… difficile de savoir quoi traiter d’abord.",
    },
  ];

  return (
    <div
      className="min-h-screen bg-[#10100F] text-[#F7F2E8] font-sans antialiased"
      data-testid="pc-consulting-landing"
    >
      {/* Navigation */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#10100F]/85 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="/logo-pc.png"
              alt="P&C Consulting"
              className="h-10 md:h-12 w-auto object-contain"
            />
            <div className="leading-tight hidden sm:block">
              <p className="font-semibold tracking-wide text-sm md:text-base">P&C Consulting</p>
              <p className="text-[11px] md:text-xs text-[#B8AD9D]">
                E-commerce & marketing digital
              </p>
            </div>
          </div>
          <nav
            className="hidden md:flex items-center gap-8 text-sm text-[#D8CCB8]"
            data-testid="primary-nav"
          >
            <a href="#probleme" className="hover:text-white transition" data-testid="nav-link-enjeux">Vos enjeux</a>
            <a href="#solution" className="hover:text-white transition" data-testid="nav-link-approche">Notre approche</a>
            <a href="#offres" className="hover:text-white transition" data-testid="nav-link-offres">Offres</a>
            <a href="#contact" className="hover:text-white transition" data-testid="nav-link-contact">Contact</a>
          </nav>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-[#F7F2E8] text-[#10100F] px-5 py-3 rounded-full text-sm font-semibold hover:bg-white transition"
            data-testid="header-cta-btn"
          >
            Diagnostic gratuit <ArrowRight size={16} />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute top-[-200px] right-[-200px] w-[520px] h-[520px] rounded-full bg-[#5E7A54]/20 blur-3xl pointer-events-none" />
        <div className="absolute bottom-[-220px] left-[-220px] w-[520px] h-[520px] rounded-full bg-white/[0.06] blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-32 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center relative">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 border border-[#5E7A54]/40 bg-[#5E7A54]/10 text-[#B8CFA8] rounded-full px-4 py-2 text-xs md:text-sm mb-7">
              <Sparkles size={14} /> Pour les e-commerçants de 500K€ à 5M€ de CA
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.02] md:leading-[0.98] font-semibold tracking-tight mb-7">
              Vous avez construit un e-commerce.{" "}
              <span className="text-[#5E7A54]">
                Maintenant, il faut le scaler.
              </span>
            </h1>

            <p className="text-lg md:text-xl text-[#D8CCB8] leading-relaxed mb-9 max-w-2xl">
              P&C Consulting devient votre direction e-commerce & acquisition
              partagée pour structurer votre croissance, piloter vos priorités
              business et accélérer vos ventes, sans recruter en interne.
            </p>

            <div className="flex flex-col sm:flex-row gap-3.5 mb-10">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#5E7A54] text-[#10100F] px-7 py-4 rounded-full font-bold text-sm md:text-base hover:bg-[#7A9970] transition shadow-xl shadow-[#5E7A54]/20"
                data-testid="hero-primary-cta"
              >
                Réserver un diagnostic gratuit <ArrowRight size={18} />
              </a>
              <a
                href="#solution"
                className="inline-flex items-center justify-center gap-2 border border-white/15 px-7 py-4 rounded-full font-semibold text-sm md:text-base text-white hover:bg-white/10 transition"
                data-testid="hero-secondary-cta"
              >
                Découvrir l’approche
              </a>
            </div>

            <div className="grid grid-cols-3 gap-4 max-w-xl">
              <div className="border-l border-[#5E7A54] pl-4">
                <p className="text-xl md:text-2xl font-bold">14M€</p>
                <p className="text-[11px] md:text-xs text-[#B8AD9D]">CA e-commerce piloté</p>
              </div>
              <div className="border-l border-[#5E7A54] pl-4">
                <p className="text-xl md:text-2xl font-bold">12 ans</p>
                <p className="text-[11px] md:text-xs text-[#B8AD9D]">d’expérience terrain</p>
              </div>
              <div className="border-l border-[#5E7A54] pl-4">
                <p className="text-xl md:text-2xl font-bold">Full funnel</p>
                <p className="text-[11px] md:text-xs text-[#B8AD9D]">de la visibilité au cash</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative"
          >
            <div className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-5 md:p-6 shadow-2xl">
              <div className="rounded-[1.5rem] bg-[#171715] border border-white/10 p-5 md:p-6">
                <p className="text-[#5E7A54] text-xs md:text-sm font-semibold mb-4 uppercase tracking-wider">
                  Votre situation actuelle
                </p>
                <div className="space-y-3">
                  {[
                    "La croissance repose trop sur vous",
                    "Les campagnes tournent, mais sans vision CAC globale",
                    "Les priorités changent chaque semaine",
                    "Vous avez besoin d’un business plan structuré pour mieux piloter",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 bg-white/[0.04] rounded-2xl p-4"
                    >
                      <CheckCircle2 className="text-[#5E7A54] shrink-0 mt-0.5" size={20} />
                      <p className="text-[#EDE4D5] text-sm md:text-base">{item}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-5 p-5 rounded-2xl bg-[#5E7A54] text-[#10100F]">
                  <p className="font-bold text-base md:text-lg">Notre rôle</p>
                  <p className="mt-1.5 text-sm leading-relaxed">
                    Vous aider à analyser, prioriser et exécuter les actions
                    qui créent de la croissance rentable.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROBLÈMES */}
      <section
        id="probleme"
        className="py-20 md:py-28 bg-[#F7F2E8] text-[#10100F]"
        data-testid="section-problemes"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl mb-12 md:mb-16">
            <p className="text-[#3D5B36] font-semibold mb-3 text-sm uppercase tracking-widest">
              Vos enjeux
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5 leading-tight">
              Vous voulez développer votre activité e-commerce mais vous ne
              savez pas quels leviers prioriser pour assurer une croissance
              réellement rentable.
            </h2>
            <p className="text-base md:text-lg text-[#5E574D] leading-relaxed">
              C’est le moment où beaucoup d’e-commerçants locaux et
              indépendants stagnent : le potentiel est là, mais le pilotage
              n’est pas encore assez structuré pour scaler sereinement.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 md:gap-6">
            {problems.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="bg-white rounded-[1.75rem] p-7 shadow-sm border border-black/5"
                data-testid={`problem-card-${title}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-[#10100F] text-[#5E7A54] flex items-center justify-center mb-5">
                  <Icon size={22} />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-3">{title}</h3>
                <p className="text-[#5E574D] leading-relaxed text-sm md:text-base">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 rounded-[2rem] bg-[#10100F] text-[#F7F2E8] p-7 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8">
            <div>
              <p className="text-[#5E7A54] font-semibold mb-2 text-sm uppercase tracking-widest">
                Le vrai sujet
              </p>
              <h3 className="text-2xl md:text-3xl font-semibold leading-tight">
                Vous n’avez pas besoin d’un énième conseil théorique.
              </h3>
              <p className="text-[#D8CCB8] mt-3 text-base md:text-lg">
                Vous avez besoin d’un copilote senior pour décider, structurer
                et faire avancer.
              </p>
            </div>
            <a
              href="#offres"
              className="inline-flex items-center justify-center gap-2 bg-[#5E7A54] text-[#10100F] px-6 py-4 rounded-full font-bold whitespace-nowrap hover:bg-[#7A9970] transition text-sm md:text-base"
              data-testid="problemes-offres-cta"
            >
              Voir les formats <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* DUO */}
      <section
        id="solution"
        className="py-20 md:py-28 bg-[#10100F]"
        data-testid="section-solution"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-4xl mx-auto mb-14 md:mb-16">
            <p className="text-[#5E7A54] font-semibold mb-3 text-sm uppercase tracking-widest">
              Notre force
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-5 leading-[1.05]">
              Le duo gagnant du e-commerce à votre disposition.
            </h2>
            <p className="text-lg md:text-xl text-[#D8CCB8] leading-relaxed">
              Deux expertises complémentaires pour couvrir tout le tunnel
              e-commerce : stratégie, rentabilité, acquisition, créa, branding
              et pilotage opérationnel.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 md:gap-8 mb-14">
            <div
              className="rounded-[2rem] bg-white/[0.06] border border-white/10 p-6 md:p-8 flex flex-col"
              data-testid="profile-chloe"
            >
              <div className="flex items-center gap-5 mb-6">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border border-white/10 bg-[#1a1a18] shrink-0">
                  <img
                    src="/team/chloe.png"
                    alt="Chloé Neyrial, direction e-commerce"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div>
                  <p className="text-[#5E7A54] font-semibold text-sm uppercase tracking-wider mb-1">
                    Chloé Neyrial
                  </p>
                  <p className="text-[#B8AD9D] text-xs md:text-sm">Direction e-commerce & stratégie</p>
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-5 leading-tight">
                Direction e-commerce, stratégie & rentabilité
              </h3>
              <ul className="space-y-3 text-[#D8CCB8] text-sm md:text-base">
                {[
                  "12 ans d’expérience e-commerce dans le retail",
                  "Création et pilotage d’une activité BtoC de zéro",
                  "Portefeuille e-commerce de 14M€",
                  "Vision P&L, marge, conversion, CRM, Shopify, ops",
                  "Capacité à prioriser et piloter les équipes/prestataires",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className="text-[#5E7A54] shrink-0 mt-0.5" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className="rounded-[2rem] bg-white/[0.06] border border-white/10 p-6 md:p-8 flex flex-col"
              data-testid="profile-pauline"
            >
              <div className="flex items-center gap-5 mb-6">
                <div className="w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden border border-white/10 bg-[#1a1a18] shrink-0">
                  <img
                    src="/team/pauline.png"
                    alt="Pauline Gubert, acquisition Meta Ads"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
                <div>
                  <p className="text-[#5E7A54] font-semibold text-sm uppercase tracking-wider mb-1">
                    Pauline Gubert
                  </p>
                  <p className="text-[#B8AD9D] text-xs md:text-sm">Acquisition & Meta Ads</p>
                </div>
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold mb-5 leading-tight">
                Direction Acquisition & Brand Strategy
              </h3>
              <ul className="space-y-3 text-[#D8CCB8] text-sm md:text-base">
                {[
                  "Fondatrice d’une agence social media à 25 ans",
                  "Expertise Meta Ads et stratégie d’acquisition",
                  "Création de contenus et angles publicitaires",
                  "Branding, désirabilité et social commerce",
                  "Connexion entre créa, trafic et performance business",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className="text-[#5E7A54] shrink-0 mt-0.5" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {[
              { icon: Target, title: "Stratégie", text: "Un cap clair et des priorités business." },
              { icon: TrendingUp, title: "Croissance", text: "Des leviers activés dans le bon ordre." },
              { icon: Euro, title: "Rentabilité", text: "Un pilotage orienté marge, cash et P&L." },
              { icon: MousePointerClick, title: "Acquisition", text: "Des campagnes et créas qui génèrent des clients." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-[1.5rem] bg-white/[0.04] border border-white/10 p-5 md:p-6"
                data-testid={`pillar-${title}`}
              >
                <Icon className="text-[#5E7A54] mb-4" size={24} />
                <h4 className="text-lg md:text-xl font-semibold mb-2">{title}</h4>
                <p className="text-[#B8AD9D] text-xs md:text-sm leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFRES */}
      <section
        id="offres"
        className="py-20 md:py-28 bg-[#F7F2E8] text-[#10100F]"
        data-testid="section-offres"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-4xl mb-12 md:mb-16">
            <p className="text-[#3D5B36] font-semibold mb-3 text-sm uppercase tracking-widest">
              Nos formats
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5 leading-tight">
              Un accompagnement clair, adapté à votre maturité et à vos
              priorités business.
            </h2>
            <p className="text-base md:text-lg text-[#5E574D] leading-relaxed">
              On commence toujours par poser les bonnes fondations (Build),
              avant d’activer les leviers d’acquisition (Growth). Ensemble,
              ils transforment votre e-commerce en moteur de croissance
              rentable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-7 items-stretch max-w-5xl mx-auto">
            {/* BUILD */}
            <article
              className="rounded-[1.75rem] p-7 md:p-9 bg-white border border-black/5 shadow-sm flex flex-col"
              data-testid="offer-card-build"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="inline-flex bg-[#10100F] text-[#5E7A54] px-3.5 py-1.5 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase">
                  01 · Build
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#3D5B36]">
                  One shot
                </span>
              </div>

              <h3 className="text-xl md:text-2xl font-semibold mb-3 leading-[1.2]">
                On restructure les fondations de croissance.
              </h3>
              <p className="text-[#5E574D] text-sm md:text-[15px] leading-relaxed mb-6">
                On analyse le business model e-commerce dans son ensemble :
                marge, CAC, stocks, logistique, cash-flow, conversion, UX,
                stack technique… pour identifier les fuites de rentabilité
                et générer du{" "}
                <em className="not-italic font-semibold text-[#10100F]">
                  “fuel for growth”
                </em>
                .
              </p>

              <div className="rounded-2xl bg-[#F7F2E8] border border-black/5 p-4 md:p-5 mb-6">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#3D5B36] mb-1.5">
                  Objectif
                </p>
                <p className="text-[#38342E] text-sm md:text-[15px] leading-relaxed">
                  Améliorer à la fois les revenus, la rentabilité et la
                  capacité à scaler durablement.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-7">
                {[
                  "Marge & rentabilité",
                  "CAC & cash-flow",
                  "UX & conversion",
                  "Stack technique",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center text-xs md:text-[13px] px-3 py-1.5 rounded-full bg-[#10100F]/5 text-[#38342E] border border-[#10100F]/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#3D5B36] mb-3">
                  Livrables
                </p>
                <ul className="space-y-3 text-[#38342E] text-sm md:text-[15px]">
                  {[
                    "P&L simplifié",
                    "Recommandations UX",
                    "Recommandations techniques",
                  ].map((b) => (
                    <li key={b} className="flex gap-3 leading-relaxed">
                      <CheckCircle2
                        className="text-[#5E7A54] shrink-0 mt-0.5"
                        size={18}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 border border-[#10100F] text-[#10100F] px-6 py-3.5 rounded-full font-semibold hover:bg-[#10100F] hover:text-[#F7F2E8] transition text-sm"
                data-testid="offer-build-cta"
              >
                Réserver un échange <ArrowRight size={16} />
              </a>
            </article>

            {/* GROWTH (FEATURED) */}
            <article
              className="rounded-[1.75rem] p-7 md:p-9 bg-[#10100F] text-[#F7F2E8] border border-[#5E7A54]/60 shadow-2xl flex flex-col relative"
              data-testid="offer-card-growth"
            >
              <div className="flex items-center gap-2 mb-6">
                <div className="inline-flex bg-[#5E7A54] text-[#10100F] px-3.5 py-1.5 rounded-full text-[11px] font-bold tracking-[0.18em] uppercase">
                  02 · Growth
                </div>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5E7A54]">
                  Forfait mensuel
                </span>
              </div>

              <h3 className="text-xl md:text-2xl font-semibold mb-3 leading-[1.2]">
                On accélère l’acquisition et la performance.
              </h3>
              <p className="text-[#D8CCB8] text-sm md:text-[15px] leading-relaxed mb-6">
                On construit des stratégies de croissance puissantes,
                notamment via Meta Ads, avec des plans d’action progressifs
                pensés pour scaler sur le long terme.
              </p>

              <div className="rounded-2xl bg-white/[0.06] border border-white/10 p-4 md:p-5 mb-6">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#5E7A54] mb-1.5">
                  Approche
                </p>
                <p className="text-[#EFE7D8] text-sm md:text-[15px] leading-relaxed">
                  On combine branding, performance marketing et IA pour créer
                  des systèmes d’acquisition plus efficaces et plus
                  résilients.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 mb-7">
                {[
                  "Meta Ads",
                  "Performance marketing",
                  "Branding",
                  "IA & automatisation",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center text-xs md:text-[13px] px-3 py-1.5 rounded-full bg-white/[0.06] text-[#EFE7D8] border border-white/10"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex-1">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#5E7A54] mb-3">
                  Livrables
                </p>
                <ul className="space-y-3 text-[#EFE7D8] text-sm md:text-[15px]">
                  {[
                    "Recommandations invest paid media",
                    "Génération de créatives",
                    "Gestion Meta Ads on demand",
                  ].map((b) => (
                    <li key={b} className="flex gap-3 leading-relaxed">
                      <CheckCircle2
                        className="text-[#5E7A54] shrink-0 mt-0.5"
                        size={18}
                      />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-[#5E7A54] text-[#10100F] px-6 py-3.5 rounded-full font-bold hover:bg-[#7A9970] transition text-sm"
                data-testid="offer-growth-cta"
              >
                Planifier un échange <ArrowRight size={16} />
              </a>
            </article>
          </div>
        </div>
      </section>

      {/* PROXIMITÉ */}
      <section className="py-20 md:py-28 bg-[#10100F]" data-testid="section-proximite">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 md:gap-12 items-center">
          <div>
            <p className="text-[#5E7A54] font-semibold mb-3 text-sm uppercase tracking-widest">
              Notre différence
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight mb-5 leading-tight">
              Proches du terrain. Orientées performance.
            </h2>
            <p className="text-lg md:text-xl text-[#D8CCB8] leading-relaxed mb-7">
              Nous accompagnons des dirigeants et petites équipes qui ont
              besoin d’un regard senior, mais aussi de concret : des décisions,
              un plan, des actions, du suivi et des arbitrages.
            </p>
            <div className="space-y-3.5">
              {[
                "Une approche adaptée aux e-commerçants indépendants",
                "Une vision business, pas uniquement marketing",
                "Un accompagnement humain pour sortir le dirigeant de l’isolement",
                "Une capacité à challenger, structurer et faire avancer",
              ].map((item) => (
                <div key={item} className="flex gap-3 text-[#EDE4D5] items-start text-sm md:text-base">
                  <Compass className="text-[#5E7A54] shrink-0 mt-1" size={20} />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-[2rem] bg-[#5E7A54] text-[#10100F] p-7 md:p-10">
            <Handshake size={36} className="mb-6" />
            <h3 className="text-2xl md:text-3xl font-semibold mb-4 leading-tight">
              Ce que vous achetez vraiment
            </h3>
            <p className="text-base md:text-lg leading-relaxed mb-6">
              Pas seulement des recommandations. Un binôme senior qui vous
              aide à faire les bons choix, dans le bon ordre, avec une
              obsession : transformer vos efforts digitaux en croissance
              rentable.
            </p>
            <div className="rounded-2xl bg-[#10100F] text-[#F7F2E8] p-5">
              <p className="font-semibold">En clair :</p>
              <p className="text-[#D8CCB8] mt-2 text-sm md:text-base">
                Vous gardez l’agilité d’une petite structure, avec le niveau
                de pilotage d’une équipe e-commerce senior.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        className="py-20 md:py-28 bg-[#F7F2E8] text-[#10100F]"
        data-testid="section-contact"
      >
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <p className="text-[#3D5B36] font-semibold mb-3 text-sm uppercase tracking-widest">
            Premier échange
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-5 leading-[1.05]">
            Parlons de vos enjeux e-commerce.
          </h2>
          <p className="text-lg md:text-xl text-[#5E574D] leading-relaxed mb-10 max-w-3xl mx-auto">
            En 30 minutes, on identifie vos priorités, vos freins actuels et
            le format d’accompagnement le plus pertinent pour vous aider à
            passer un cap.
          </p>
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#10100F] text-[#F7F2E8] px-7 md:px-8 py-4 md:py-5 rounded-full font-bold text-base md:text-lg hover:bg-black transition"
            data-testid="contact-calendly-btn"
          >
            Réserver un diagnostic gratuit <ArrowRight size={20} />
          </a>
          <p className="mt-7 text-sm md:text-base text-[#5E574D]">
            contact@pc-consulting.fr
          </p>
        </div>
      </section>

      <footer className="bg-[#10100F] border-t border-white/10 py-7 text-center text-[#B8AD9D] text-xs md:text-sm px-5">
        © 2026 P&C Consulting, Direction e-commerce & acquisition partagée
      </footer>
    </div>
  );
}
